import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { signInWithGoogle, signInWithApple } from '@/services/auth'
import { acceptOAuthInvitation } from '@/services/enhancedInvitations'
import { logger } from '@/services/logger'
import { afterAcceptPath } from '@/services/twoFactor'
import { reportAuthFailure } from '@/services/authFailureLog'

type OAuthProvider = 'google' | 'apple'

interface OAuthInvitationResult {
  handleOAuthSignIn: (provider: OAuthProvider) => Promise<void>
  submitting: boolean
  error: string
  setError: (error: string) => void
}

interface UseOAuthInvitationProps {
  invitationId: string | null
  /** The invitation's role: admins go on to the phone step (PB-88). */
  role?: string
}

/**
 * Unified OAuth invitation acceptance hook
 * Handles sign-in flow for Google and Apple OAuth providers
 * and automatically accepts the invitation upon successful authentication
 */
export function useOAuthInvitation({
  invitationId,
  role,
}: UseOAuthInvitationProps): OAuthInvitationResult {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const providerSignInMap = {
    google: signInWithGoogle,
    apple: signInWithApple,
  }

  const providerDisplayNames = {
    google: 'Google',
    apple: 'Apple',
  }

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    setError('')
    setSubmitting(true)

    try {
      // Validate invitation ID
      if (!invitationId) {
        setError('Invalid invitation link')
        setSubmitting(false)
        return
      }

      const providerName = providerDisplayNames[provider]
      logger.debug(
        `[AcceptInvitation] Starting ${providerName} OAuth sign-in...`
      )

      const signInFunction = providerSignInMap[provider]
      const result = await signInFunction({
        skipUserCreation: true,
        // For redirect-based OAuth (Amazon), preserve the invitation context
        returnPath: `/accept-invitation?id=${invitationId}`,
      })

      if (result.success && result.user) {
        logger.debug(
          `[AcceptInvitation] ${providerName} OAuth successful, user:`,
          {
            uid: result.user.uid,
            email: result.user.email,
          }
        )

        logger.debug('[AcceptInvitation] Accepting invitation...')
        const acceptResult = await acceptOAuthInvitation(
          invitationId as string, // Guaranteed to be non-null due to check above
          result.user.uid,
          result.user.email ?? undefined
        )

        if (acceptResult.success) {
          logger.debug('[AcceptInvitation] ✅ Invitation accepted successfully')
          toast.success('Welcome!')
          navigate(afterAcceptPath(role))
        } else {
          console.error(
            '[AcceptInvitation] ❌ Failed to accept invitation:',
            acceptResult.error
          )
          reportAuthFailure('accept_invitation', new Error(acceptResult.error))
          setError(acceptResult.error || 'Failed to accept invitation')
          setSubmitting(false)
        }
      } else {
        console.error(
          `[AcceptInvitation] ❌ ${providerName} OAuth failed:`,
          result.error
        )
        reportAuthFailure(`${provider}_sign_in`, new Error(result.error))
        setError(result.error || `Failed to sign in with ${providerName}`)
        setSubmitting(false)
      }
    } catch (err) {
      const providerName = providerDisplayNames[provider]
      console.error(
        `[AcceptInvitation] ❌ Error with ${providerName} sign-in:`,
        err
      )
      reportAuthFailure('accept_invitation', err)
      setError(
        `Failed to sign in with ${providerName}: ${(err as Error).message}`
      )
      setSubmitting(false)
    }
  }

  return {
    handleOAuthSignIn,
    submitting,
    error,
    setError,
  }
}
